import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Icon } from '../../../../core/components/atoms/Icon';
import { useReviewStore } from '../../stores/review';
import { formatTime } from '../../../../core/commom/formatTime';

export interface ProgressBarHandle {
  stop: () => void | number; // Add return type to fix TypeScript error
}

interface ProgressBarProps {
  totalQuestions: number;
  currentQuestion: number;
}

const ProgressBar = forwardRef<ProgressBarHandle, ProgressBarProps>(
  ({ totalQuestions, currentQuestion }, ref) => {
    const { startReviewAt } = useReviewStore();
    const [progressTime, setProgressTime] = useState(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const progressPercentage = Math.min(
      (currentQuestion / totalQuestions) * 100,
      100
    );

    useEffect(() => {
      if (!startReviewAt) return;

      const startReviewAtDate = new Date(startReviewAt);

      timerRef.current = setInterval(() => {
        const now = new Date();
        const diffSeconds = Math.floor(
          (now.getTime() - startReviewAtDate.getTime()) / 1000
        );
        setProgressTime(diffSeconds);
      }, 1000);

      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }, [startReviewAt]);

    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      return progressTime;
    };

    useImperativeHandle(ref, () => ({
      stop,
    }));

    return (
      <div className='font-sans mx-auto mt-6 w-full max-w-[748px]'>
        <div className='h-3 w-full overflow-hidden rounded-pill bg-[#E5E7EB]'>
          <div
            className='h-full rounded-pill transition-all duration-300 ease-in-out'
            style={{
              width: `${progressPercentage}%`,
              background: 'linear-gradient(360deg, #00B8AB 0%, #84D9B2 100%)',
            }}
          ></div>
        </div>
        <div className='mb-1 flex justify-between'>
          <div className='flex items-center gap-2'>
            <Icon
              Icon='timer'
              type='material'
              className='text-md text-mono-color-dark'
            />
            <Typography className='text-h6 text-mono-color-dark'>
              {formatTime(progressTime)}
            </Typography>
          </div>
          <Typography className='text-h6 text-mono-color-dark'>
            {currentQuestion}/{totalQuestions}
          </Typography>
        </div>
      </div>
    );
  }
);
ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
